import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Plus, Edit, Trash2, Save, X, Upload } from 'lucide-react';

const TeamManager = () => {
    const [team, setTeam] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [currentMember, setCurrentMember] = useState(null);
    const [imageFile, setImageFile] = useState(null);

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        role: '',
        instagram: '',
        twitter: '',
        linkedin: ''
    });

    useEffect(() => {
        fetchTeam();
    }, []);

    const fetchTeam = async () => {
        try {
            const { data } = await api.get('/team');
            setTeam(data);
            setLoading(false);
        } catch (error) {
            console.error(error);
            setLoading(false);
        }
    };

    const handleEdit = (member) => {
        setCurrentMember(member);
        setFormData({
            name: member.name,
            role: member.role,
            instagram: member.socials?.instagram || '',
            twitter: member.socials?.twitter || '',
            linkedin: member.socials?.linkedin || ''
        });
        setIsEditing(true);
        setImageFile(null);
    };

    const handleAddNew = () => {
        setCurrentMember(null);
        setFormData({
            name: '',
            role: '',
            instagram: '',
            twitter: '',
            linkedin: ''
        });
        setIsEditing(true);
        setImageFile(null);
    };

    const handleDelete = async (id) => {
        if (confirm('Are you sure you want to delete this member?')) {
            try {
                await api.delete(`/team/${id}`);
                fetchTeam();
            } catch (error) {
                console.error(error);
                alert('Failed to delete member');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const data = new FormData();
        data.append('name', formData.name);
        data.append('role', formData.role);
        data.append('instagram', formData.instagram);
        data.append('twitter', formData.twitter);
        data.append('linkedin', formData.linkedin);
        if (imageFile) {
            data.append('image', imageFile);
        }

        try {
            if (currentMember) {
                await api.put(`/team/${currentMember._id}`, data);
            } else {
                await api.post('/team', data);
            }
            setIsEditing(false);
            fetchTeam();
        } catch (error) {
            console.error(error);
            alert('Failed to save team member');
        }
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">Team Management</h1>
                <button onClick={handleAddNew} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
                    <Plus className="w-4 h-4" /> Add Member
                </button>
            </div>

            {isEditing && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg w-full max-w-md">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">{currentMember ? 'Edit Member' : 'Add Member'}</h2>
                            <button onClick={() => setIsEditing(false)}><X className="w-5 h-5" /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Name</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full border rounded p-2"
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Role</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full border rounded p-2"
                                    value={formData.role}
                                    onChange={e => setFormData({ ...formData, role: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Photo</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={e => setImageFile(e.target.files[0])}
                                    className="w-full"
                                />
                            </div>

                            <div className="pt-2 border-t">
                                <p className="text-sm font-semibold mb-2 text-gray-500">Social Links (Optional)</p>
                                <div className="space-y-2">
                                    <input
                                        type="text"
                                        placeholder="Instagram URL"
                                        className="w-full border rounded p-2 text-sm"
                                        value={formData.instagram}
                                        onChange={e => setFormData({ ...formData, instagram: e.target.value })}
                                    />
                                    <input
                                        type="text"
                                        placeholder="Twitter URL"
                                        className="w-full border rounded p-2 text-sm"
                                        value={formData.twitter}
                                        onChange={e => setFormData({ ...formData, twitter: e.target.value })}
                                    />
                                    <input
                                        type="text"
                                        placeholder="LinkedIn URL"
                                        className="w-full border rounded p-2 text-sm"
                                        value={formData.linkedin}
                                        onChange={e => setFormData({ ...formData, linkedin: e.target.value })}
                                    />
                                </div>
                            </div>

                            <button type="submit" className="w-full bg-black text-white py-2 rounded hover:bg-gray-800 transition-colors">
                                {currentMember ? 'Update Member' : 'Add Member'}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {team.map(member => (
                    <div key={member._id} className="bg-white p-4 rounded-lg shadow border flex flex-col items-center text-center">
                        <div className="w-20 h-20 rounded-full bg-gray-200 mb-3 overflow-hidden">
                            {member.image && (
                                <img
                                    src={member.image.startsWith('http') ? member.image : `${import.meta.env.VITE_API_URL.replace('/api', '')}${member.image}`}
                                    alt={member.name}
                                    className="w-full h-full object-cover"
                                />
                            )}
                        </div>
                        <h3 className="font-bold">{member.name}</h3>
                        <p className="text-sm text-gray-500 mb-3">{member.role}</p>

                        <div className="flex gap-2 mt-auto">
                            <button onClick={() => handleEdit(member)} className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                                <Edit className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleDelete(member._id)} className="p-2 text-red-600 hover:bg-red-50 rounded">
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TeamManager;
